/**
 * Matchmaking Manager
 *
 * Handles the matchmaking flow for Quickstart:
 * - Lobby creation
 * - Lobby joining
 * - Matchmaking start
 * - Match found handling
 * - Broadcast ready detection
 *
 * This is feature-specific to Quickstart and knows about:
 * - Playcast pavilion/matchmaker system
 * - Global profile/join state stores
 * - Match timeouts and failure conditions
 */

import {
  createMatchmakerLobby,
  joinMatchmakerLobby,
  startMatchmakerLobby,
  destroyMatchmakerLobby,
} from '../../../api/modules/realtimeMessaging/handlers/profileMessage';
import { profileState } from '../../../api/stores/lobbyStores';
import { authStore, joinState } from '../../../api/stores/globalStores';
import { playerConnectionState } from '../../../api/stores/playerStores';
import { setPlayerBroadcastId } from '../../../api/modules/playerObservability';
import type { QuickstartState } from '../QuickstartController';

// Declare global type for auth upgrade tracking (from conectionMessage.ts)
declare global {
  interface Window {
    pendingAuthUpgradeSocket: WebSocket | null;
  }
}

export interface MatchmakingManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Transition to a new phase
   */
  transitionTo: (phase: string) => void;

  /**
   * Called when matchmaking fails
   */
  onMatchmakingFailed: (reason: string) => void;

  /**
   * Called when broadcast ready and should connect
   */
  onBroadcastReady: (broadcastId: string) => void;

  /**
   * Called when the invitation becomes invalid during play
   */
  onInvitationInvalidated?: () => void;

  /**
   * Send analytics events
   */
  sendMatchmakingStarted: (matchmakingTimeout: number) => void;
  sendMatchmakingSucceeded: () => void;
}

/**
 * Manager for matchmaking flow
 */
export class MatchmakingManager {
  private config: MatchmakingManagerConfig;
  private matchmakingTimeout?: NodeJS.Timeout;
  private matchFoundTimeout?: NodeJS.Timeout;
  private createdInvitationId?: string;
  private storeListeners: string[] = [];
  private debugStatusInterval?: NodeJS.Timeout;

  // Shadow architecture: Track invitation status independently
  private trackedInvitationId?: string;
  private isInvitationInvalid = false;

  constructor(config: MatchmakingManagerConfig) {
    this.config = config;
  }

  /**
   * Start the matchmaking flow
   */
  public async startMatchmaking(): Promise<void> {
    // Subscribe to stores for match notifications
    this.setupStoreSubscriptions();

    // Start debug status logging (every 1 second)
    this.startDebugStatusLogging();

    // Transition to creating lobby
    this.config.transitionTo('creating_lobby');

    // Wait for auth if needed
    await this.waitForAuth();

    // Create lobby
    createMatchmakerLobby();
  }

  /**
   * Wait for auth to be ready if user is signed in
   *
   * Shadow architecture: Detect and wait for auth upgrades to prevent race condition
   * where invitations are created with anonymous owner while upgrade is in flight
   */
  private async waitForAuth(): Promise<void> {
    return new Promise((resolve) => {
      // Check if auth upgrade is in progress (race condition prevention)
      if (typeof window !== 'undefined' && window.pendingAuthUpgradeSocket) {
        console.log('[MatchmakingManager] Auth upgrade in progress, waiting for completion...');

        // Wait for AUTH_UPGRADE_SUCCESS by monitoring playerAuthId changes
        let upgradeCheckCount = 0;
        const maxUpgradeChecks = 20; // 2 seconds max (20 * 100ms)

        const checkUpgradeComplete = () => {
          upgradeCheckCount++;

          // Check if upgrade completed (pendingAuthUpgradeSocket cleared)
          if (!window.pendingAuthUpgradeSocket) {
            console.log('[MatchmakingManager] Auth upgrade completed, playerAuthId:', authStore.currentState.playerAuthId);
            resolve();
            return;
          }

          // Timeout after max checks
          if (upgradeCheckCount >= maxUpgradeChecks) {
            console.warn('[MatchmakingManager] Auth upgrade timeout, proceeding anyway');
            resolve();
            return;
          }

          // Check again in 100ms
          setTimeout(checkUpgradeComplete, 100);
        };

        checkUpgradeComplete();
        return;
      }

      // Check if we already have auth
      if (authStore.currentState.playerAuthId) {
        console.log('[MatchmakingManager] Auth ready:', authStore.currentState.playerAuthId);
        resolve();
        return;
      }

      console.log('[MatchmakingManager] Waiting for auth...');

      // Subscribe to auth changes
      const listenerId = authStore.addListener((state) => {
        if (state.playerAuthId) {
          authStore.removeListener(listenerId);
          console.log('[MatchmakingManager] Auth ready:', state.playerAuthId);
          resolve();
        }
      });

      // Timeout after 500ms (anonymous flow)
      setTimeout(() => {
        authStore.removeListener(listenerId);
        console.log('[MatchmakingManager] No auth (anonymous flow)');
        resolve();
      }, 500);
    });
  }

  /**
   * Start debug status logging (every 1 second)
   */
  private startDebugStatusLogging(): void {
    this.debugStatusInterval = setInterval(() => {
      const state = this.config.getState();
      const profileStore = profileState.currentState;
      const joinStore = joinState.currentState;
      const authState = authStore.currentState;

      const elapsedMatchmaking = state.matchmakingStartTime
        ? Math.round((Date.now() - state.matchmakingStartTime) / 1000)
        : 0;

      console.log('[MatchmakingManager] Status Check:', {
        phase: state.phase,
        lobbyCreated: state.lobbyCreated,
        lobbyJoined: state.lobbyJoined,
        matchId: state.matchId,
        broadcastId: state.broadcastId,
        createdInvitationId: this.createdInvitationId,
        trackedInvitationId: this.trackedInvitationId,
        matchmakingElapsedSeconds: elapsedMatchmaking,
        profileStore: {
          creating: profileStore.creating,
          selectedInvitationId: profileStore.selectedInvitationId,
          matchFoundId: profileStore.matchFoundId,
          matchBroadcastId: profileStore.matchBroadcastId,
        },
        joinStore: {
          state: joinStore.state,
        },
        authState: {
          playerAuthId: authState.playerAuthId,
        },
      });
    }, 1000);
  }

  /**
   * Setup subscriptions to global stores
   */
  private setupStoreSubscriptions(): void {
    // Profile state (lobby creation, match found, broadcast ready)
    const profileListenerId = profileState.addListener((state) => {
      this.handleProfileStateChange(state);
    });
    this.storeListeners.push(profileListenerId);

    // Join state (connection status)
    const joinListenerId = joinState.addListener((state) => {
      this.handleJoinStateChange(state);
    });
    this.storeListeners.push(joinListenerId);

    // Player connection state
    const playerListenerId = playerConnectionState.addListener((state) => {
      this.handlePlayerConnectionStateChange(state);
    });
    this.storeListeners.push(playerListenerId);
  }

  /**
   * Handle profile state changes
   */
  private handleProfileStateChange(state: typeof profileState.currentState): void {
    const currentState = this.config.getState();
    const currentPhase = currentState.phase;

    // Check if our tracked invitation was explicitly invalidated by backend
    // The backend sends MATCHMAKER_INVITATION_UPDATED with invalid:true which sets lastInvalidatedInvitationId
    const stateWithInvalidation = state as typeof state & { lastInvalidatedInvitationId?: string };
    if (stateWithInvalidation.lastInvalidatedInvitationId === this.trackedInvitationId && !this.isInvitationInvalid) {
      console.log('[MatchmakingManager] Tracked invitation invalidated by backend:', this.trackedInvitationId);
      this.isInvitationInvalid = true;

      // During play - exit to CTA
      if (currentPhase === 'playing_afk' || currentPhase === 'playing_active') {
        console.log('[MatchmakingManager] Invitation invalidated during play - triggering exit to CTA');
        if (this.config.onInvitationInvalidated) {
          this.config.onInvitationInvalidated();
        }
      }
      // During matchmaking - fail matchmaking
      else if (currentPhase === 'matchmaking' || currentPhase === 'match_found' || currentPhase === 'joining_broadcast') {
        console.log('[MatchmakingManager] Invitation invalidated during matchmaking - failing');
        this.config.onMatchmakingFailed('Invitation was invalidated');
      }
    }

    // Lobby created
    if (!currentState.lobbyCreated && state.creating === false && state.selectedInvitationId) {
      console.log('[MatchmakingManager] Lobby created:', state.selectedInvitationId);
      this.createdInvitationId = state.selectedInvitationId;
      this.trackedInvitationId = state.selectedInvitationId; // Track this invitation
      this.config.updateState({ lobbyCreated: true });
      this.joinLobby();
    }

    // Match found
    if (
      state.matchFoundId &&
      !currentState.matchId &&
      currentPhase === 'matchmaking' &&
      state.matchFoundId === state.selectedInvitationId
    ) {
      console.log('[MatchmakingManager] Match found:', state.matchFoundId);
      this.handleMatchFound(state.matchFoundId);
    }

    // Broadcast ready
    if (
      state.matchBroadcastId &&
      !currentState.broadcastId &&
      currentPhase === 'match_found' &&
      state.matchFoundId === currentState.matchId
    ) {
      console.log('[MatchmakingManager] Broadcast ready:', state.matchBroadcastId);
      this.handleBroadcastReady(state.matchBroadcastId);
    }
  }

  /**
   * Handle join state changes
   */
  private handleJoinStateChange(state: typeof joinState.currentState): void {
    if (state.state === 'hostDisconnected' || state.state === 'error') {
      const currentPhase = this.config.getState().phase;
      if (currentPhase === 'joining_broadcast') {
        console.error('[MatchmakingManager] Connection failed via joinState');
      }
    }
  }

  /**
   * Handle player connection state changes
   */
  private handlePlayerConnectionStateChange(state: typeof playerConnectionState.currentState): void {
    if (state.status === 'connected') {
      const currentPhase = this.config.getState().phase;
      if (currentPhase === 'joining_broadcast') {
        this.config.updateState({ playerConnectionId: state.playerPeerId });
      }
    }
  }

  /**
   * Join the created lobby
   */
  private joinLobby(): void {
    joinMatchmakerLobby();
    this.config.updateState({ lobbyJoined: true });
    this.startMatchmakingSearch();
  }

  /**
   * Start matchmaking search
   */
  private startMatchmakingSearch(): void {
    this.config.transitionTo('matchmaking');

    const startTime = Date.now();
    const matchmakingTimeoutSeconds = 60;
    this.config.updateState({ matchmakingStartTime: startTime });
    this.config.sendMatchmakingStarted(matchmakingTimeoutSeconds);

    // Start matchmaking
    startMatchmakerLobby(false, undefined);

    // Set timeout (60 seconds)
    this.matchmakingTimeout = setTimeout(() => {
      if (this.config.getState().phase === 'matchmaking') {
        this.config.onMatchmakingFailed('Matchmaking timeout - no available hosts');
      }
    }, matchmakingTimeoutSeconds * 1000);
  }

  /**
   * Handle match found
   */
  private handleMatchFound(matchId: string): void {
    const currentState = this.config.getState();
    const duration = currentState.matchmakingStartTime
      ? Date.now() - currentState.matchmakingStartTime
      : 0;

    // Shadow architecture: Track the matched invitation for invalidation detection
    this.trackedInvitationId = matchId;

    this.config.updateState({
      matchId,
      matchmakingDuration: duration,
    });

    this.config.transitionTo('match_found');
    this.config.sendMatchmakingSucceeded();

    // Clear matchmaking timeout
    if (this.matchmakingTimeout) {
      clearTimeout(this.matchmakingTimeout);
      this.matchmakingTimeout = undefined;
    }

    // Set timeout for broadcast ready (15 seconds)
    this.matchFoundTimeout = setTimeout(() => {
      if (this.config.getState().phase === 'match_found') {
        console.error('[MatchmakingManager] Broadcast ready timeout');
        this.destroyInvitation();
        this.config.onMatchmakingFailed('Match found but broadcast never became ready');
      }
    }, 15000);
  }

  /**
   * Handle broadcast ready
   */
  private handleBroadcastReady(broadcastId: string): void {
    // Clear match found timeout
    if (this.matchFoundTimeout) {
      clearTimeout(this.matchFoundTimeout);
      this.matchFoundTimeout = undefined;
    }

    this.config.updateState({ broadcastId });
    setPlayerBroadcastId(broadcastId);

    // Clear invitation ID - broadcast is ready
    console.log('[MatchmakingManager] Broadcast ready, clearing invitation to prevent cleanup destruction');
    this.createdInvitationId = undefined;

    // Notify coordinator to start connection
    this.config.onBroadcastReady(broadcastId);
  }

  /**
   * Retry matchmaking (after failure)
   */
  public retry(): void {
    console.log('[MatchmakingManager] Retrying matchmaking');

    // Destroy old invitation if it exists
    this.destroyInvitation();

    // Reset state
    this.config.updateState({
      lobbyCreated: false,
      lobbyJoined: false,
      matchId: undefined,
      broadcastId: undefined,
      matchmakingStartTime: undefined,
      matchmakingDuration: undefined,
      errorMessage: undefined,
    });

    // Start fresh
    this.startMatchmaking();
  }

  /**
   * Destroy the created invitation
   */
  private destroyInvitation(): void {
    if (this.createdInvitationId) {
      console.log('[MatchmakingManager] Destroying invitation:', this.createdInvitationId);
      try {
        destroyMatchmakerLobby(this.createdInvitationId);
      } catch (error) {
        console.error('[MatchmakingManager] Error destroying invitation:', error);
      }
      this.createdInvitationId = undefined;
    }
  }

  /**
   * Cleanup matchmaking resources
   */
  public cleanup(): void {
    // Clear debug status interval
    if (this.debugStatusInterval) {
      clearInterval(this.debugStatusInterval);
      this.debugStatusInterval = undefined;
    }

    // Clear timeouts
    if (this.matchmakingTimeout) {
      clearTimeout(this.matchmakingTimeout);
      this.matchmakingTimeout = undefined;
    }

    if (this.matchFoundTimeout) {
      clearTimeout(this.matchFoundTimeout);
      this.matchFoundTimeout = undefined;
    }

    // Unsubscribe from stores
    if (this.storeListeners.length > 0) {
      profileState.removeListener(this.storeListeners[0]);
      joinState.removeListener(this.storeListeners[1]);
      playerConnectionState.removeListener(this.storeListeners[2]);
      this.storeListeners = [];
    }

    // Reset tracking variables
    this.trackedInvitationId = undefined;
    this.isInvitationInvalid = false;

    // Destroy invitation if still exists
    this.destroyInvitation();
  }
}

